import React, { createContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Container,
  Badge,
  Spinner,
  Alert,
  Button,
} from "reactstrap";
import classNames from "classnames";
import CustomerServices from "../../../services/OrderingServices/CustomerServices";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CustomerCreditLogs from "../Components/CustomerCreditLogs.jsx";
import CustomerForm from "../Components/CustomerForm.jsx";

export const CustomerContext = createContext();

// Reusable InfoRow component matching profile design
const InfoRow = ({ label, value, valueClass = "" }) => (
  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
    <span className="text-muted" style={{ fontSize: "14px" }}>
      {label}:
    </span>
    <span className={`fw-medium ${valueClass}`} style={{ fontSize: "14px" }}>
      {value}
    </span>
  </div>
);

const CustomerDetail = () => {
  const { id } = useParams();

  const [customerDetail, setCustomerDetail] = useState();
  const [activeTab, setActiveTab] = useState("1");
  const [itemDetailLoaded, setItemDetailLoaded] = useState(false);
  const [addressDetailLoaded, setAddressDetailLoaded] = useState(false);
  const [paymentDetailLoaded, setPaymentDetailLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [creditDateNepFrom, setCreditDateNepFrom] = useState("");
  const [creditDateNepTo, setCreditDateNepTo] = useState("");

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [creditLogs, setCreditLogs] = useState("");

  const loadCreditLogs = async () => {
    try {
      setLoading(true);

      const logs = await CustomerServices.getCreditLogs(id, {
        startDate,
        endDate,
        creditDateNepFrom,
        creditDateNepTo,
      });

      setCreditLogs(logs);
    } catch (err) {
      console.log("Error loading credit logs", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCreditLogs();
  }, [id, startDate, endDate, creditDateNepFrom, creditDateNepTo]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = await CustomerServices.getCustomerDetail(id);
        console.log("🚀 ~ response:", response);
        setCustomerDetail(response);
        setFetchError(null);
      } catch (error) {
        console.log("🚀 ~ error:", error);
        setFetchError("Failed to load customer data. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const breadcrumbItems = [{ title: "Back To List", link: "/customer-list" }];

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      if (tab === "2" && !itemDetailLoaded) {
        setItemDetailLoaded(true);
      }
      if (tab === "3" && !addressDetailLoaded) {
        setAddressDetailLoaded(true);
      }
      if (tab === "4" && !paymentDetailLoaded) {
        setPaymentDetailLoaded(true);
      }
      setActiveTab(tab);
    }
  };

  const toggleEditModal = () => {
    setIsEditModalOpen((prev) => !prev);
  };

  const customer = customerDetail?.customer;

  return (
    <CustomerContext.Provider value={customerDetail}>
      <Container fluid>
        <div className="page-content">
          <BreadCrumb
            pageTitle="Customer"
            title="Customer Detail"
            breadcrumbItems={breadcrumbItems}
          />

          {/* Error Alert */}
          {fetchError && (
            <Alert color="danger" className="mb-4">
              {fetchError}
            </Alert>
          )}

          {loading ? (
            <Card className="shadow-sm border-0">
              <CardBody className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-3 text-muted">Loading customer data...</p>
              </CardBody>
            </Card>
          ) : (
            <>
              {/* Main Customer Profile Card */}
              <Card className="shadow-sm border-0 mb-4">
                <CardBody className="p-4">
                  <Row>
                    {/* Left Column - Avatar and Basic Info */}
                    <Col lg={4} className="text-center mb-4 mb-lg-0">
                      <div className="position-relative d-inline-block">
                        {/* Customer Avatar */}
                        <div
                          className="d-inline-flex align-items-center justify-content-center bg-light rounded-circle"
                          style={{
                            width: "150px",
                            height: "150px",
                            border: "4px solid #f0f0f0",
                          }}
                        >
                          <i
                            className="ri-user-line"
                            style={{ fontSize: "70px", color: "#002538" }}
                          ></i>
                        </div>
                      </div>

                      <h4 className="mt-3 mb-1">
                        {customer?.firstName} {customer?.lastName}
                      </h4>
                      <Badge
                        color="info"
                        className="px-3 py-2"
                        style={{ fontSize: "12px" }}
                      >
                        {customer?.customerTypeName || "Customer"}
                      </Badge>
                    </Col>

                    {/* Right Column - Detailed Information */}
                    <Col lg={8}>
                      <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                        <h5 className="mb-0">Customer Information</h5>
                        <Button
                          color="light"
                          className="btn-sm"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setIsEditModalOpen(true);
                          }}
                        >
                          <i className="ri-edit-line"></i>
                        </Button>
                      </div>

                      <Row>
                        <Col md={6}>
                          <InfoRow
                            label="Email"
                            value={customer?.email || "N/A"}
                          />
                          <InfoRow
                            label="Phone Number"
                            value={customer?.phoneNo || "N/A"}
                          />
                          {customer?.phoneNo2 && (
                            <InfoRow
                              label="Secondary Phone"
                              value={customer.phoneNo2}
                            />
                          )}{" "}
                          <InfoRow
                            label="Address"
                            value={customer?.address || "N/A"}
                          />
                          <InfoRow
                            label="Customer Type"
                            value={customer?.customerTypeName || "N/A"}
                          />
                        </Col>
                        <Col md={6}>
                          {customer?.customerTypeDiscount > 0 && (
                            <InfoRow
                              label="Discount"
                              value={`${customer.customerTypeDiscount}%`}
                              valueClass="text-success"
                            />
                          )}
                          <InfoRow
                            label="Total Orders"
                            value={customer?.totalOrders || 0}
                          />{" "}
                          <InfoRow
                            label="Total Order Amount"
                            value={`Rs. ${
                              customer?.totalOrderAmount?.toFixed(2) || "0.00"
                            }`}
                            valueClass="text-success fw-bold"
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Tabs Section */}
              <div
                className="p-3 p-lg-4"
                style={{
                  background: "#ffffff",
                  borderRadius: "0.5rem",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  border: "1px solid #eee",
                }}
              >
                <Row className="py-auto">
                  <Col lg={12}>
                    <Nav
                      pills
                      className="animation-nav profile-nav gap-2 gap-lg-3 flex-grow-1"
                      role="tablist"
                    >
                      <NavItem>
                        <NavLink
                          href="#"
                          className={classNames({
                            active: activeTab === "1",
                          })}
                          onClick={() => {
                            toggleTab("1");
                          }}
                          title="Credit Logs"
                        >
                          <i className="ri-airplay-fill d-inline-block d-md-none"></i>{" "}
                          <span
                            className="d-none d-md-inline-block fw-bold"
                            style={{ color: "rgb(0,37,56)" }}
                          >
                            Credit Logs
                          </span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#"
                          className={classNames({
                            active: activeTab === "2",
                          })}
                          onClick={() => {
                            toggleTab("2");
                          }}
                          title="Item Detail"
                        >
                          <i className="ri-list-unordered d-inline-block d-md-none"></i>{" "}
                          <span
                            className="fw-bold"
                            style={{ color: "rgb(0,37,56)" }}
                          >
                            Item Detail
                          </span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#"
                          className={classNames({
                            active: activeTab === "3",
                          })}
                          onClick={() => {
                            toggleTab("3");
                          }}
                          title="Delivery Detail"
                        >
                          <i className="ri-list-unordered d-inline-block d-md-none"></i>{" "}
                          <span
                            className="d-none d-md-inline-block fw-bold"
                            style={{ color: "rgb(0,37,56)" }}
                          >
                            Delivery Detail
                          </span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#"
                          className={classNames({
                            active: activeTab === "4",
                          })}
                          onClick={() => {
                            toggleTab("4");
                          }}
                          title="Payment Detail"
                        >
                          <i className="ri-list-unordered d-inline-block d-md-none"></i>{" "}
                          <span
                            // className="d-none d-md-inline-block fw-bold"
                            style={{ color: "rgb(0,37,56)" }}
                          >
                            Payment Detail
                          </span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                    <TabContent
                      activeTab={activeTab}
                      className="pt-4 text-muted"
                    >
                      <TabPane tabId="1">
                        <CustomerCreditLogs
                          creditLogs={creditLogs}
                          loading={loading}
                        />
                      </TabPane>
                      <TabPane tabId="2">
                        {/* {itemDetailLoaded && <ItemDetail />} */}
                      </TabPane>
                      <TabPane tabId="3">
                        {/* {addressDetailLoaded && <AddressDetail />} */}
                      </TabPane>
                      <TabPane tabId="4">
                        {/* {paymentDetailLoaded && <PaymentDetail />} */}
                      </TabPane>
                    </TabContent>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </div>
        <CustomerForm
          modal={isEditModalOpen}
          toggleModal={toggleEditModal}
          selectedCustomer={selectedCustomer}
          fetchCustomers={async () => {
            const response = await CustomerServices.getCustomerDetail(id);
            setCustomerDetail(response);
          }}
        />
      </Container>
    </CustomerContext.Provider>
  );
};

export default CustomerDetail;
